<script lang="ts">
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';

	const stories = [
		{
			id: 'zivrio',
			name: 'Zivrio',
			logo: '/logos/zivrio.png',
			website: 'https://zivrio.com',
			industry: 'Software Consultancy',
			location: 'Sweden',
			size: '1–10 people',
			tags: ['Distributed team', 'Client billing', 'Time tracking'],
			brandColor: '#132130',
			heroBanner: '/logos/zivrio-banner.jpg',
			quote: 'Zivrio tightened up billing and spent less time explaining invoices to clients.',
			author: 'Robert Blixt',
			role: 'Founder',
			excerpt: 'A Node.js and .NET consultancy running across continents. Heyweek tied together their time tracking, client billing, and project work — and billing accuracy improved as a result.',
			slug: 'zivrio'
		}
	];
</script>

<svelte:head>
	<title>Customer Stories &amp; Case Studies — Heyweek</title>
	<meta name="description" content="See how real teams and freelancers use Heyweek to track time, manage projects, invoice clients, and bill with confidence — read their success stories." />
	<meta property="og:title" content="Customer Stories — Heyweek" />
	<meta property="og:description" content="See how real teams and freelancers use Heyweek to track time, manage projects, invoice clients, and bill with confidence — read their success stories." />
</svelte:head>

<Header />

<main class="page">

	<!-- Hero -->
	<section class="hero">
		<div class="container">
			<p class="hero__eyebrow">Customer stories</p>
			<h1 class="hero__title">Real work.<br>Real results.</h1>
		</div>
	</section>

	<!-- Stories -->
	<section class="stories">
		<div class="container">
			{#each stories as story}
				<a class="story" href="/stories/{story.slug}">

					<!-- Left panel: identity -->
					<div class="story__identity">
						<img src={story.logo} alt="{story.name} logo" class="story__logo" />
					</div>

					<!-- Right panel: quote -->
					<div class="story__content">
						<p class="story__quote">{story.quote}</p>
						<span class="story__cta">
							Read story
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
								<line x1="5" y1="12" x2="19" y2="12"></line>
								<polyline points="12 5 19 12 12 19"></polyline>
							</svg>
						</span>
					</div>

				</a>
			{/each}
		</div>
	</section>

	<!-- Invitation -->
	{#if stories.length < 5}
		<section class="invite">
			<div class="container">
				<div class="invite__inner">
					<div class="invite__text">
						<h2>Want to be featured?</h2>
						<p>We work with a handful of teams to write honest stories about how they use Heyweek in the real world. If that sounds like you, get in touch.</p>
					</div>
					<Button href="/contact" variant="secondary" size="md">Get in touch</Button>
				</div>
			</div>
		</section>
	{/if}

	<!-- CTA -->
	<section class="cta">
		<div class="container">
			<div class="cta__inner">
				<h2>Try it yourself</h2>
				<p>Free during beta. No credit card required.</p>
				<div class="cta__actions">
					<Button href="https://app.heyweek.com/?signup" variant="primary" size="lg">Start free</Button>
					<Button href="/pricing" variant="ghost" size="lg">See pricing</Button>
				</div>
			</div>
		</div>
	</section>

</main>

<SeoContent slug="customers" />

<Footer />

<style>
	.page {
		min-height: 100vh;
	}

	.container {
		max-width: 1100px;
		margin-inline: auto;
		padding-inline: var(--container-padding, 1.5rem);
	}

	/* ── Hero ── */
	.hero {
		padding-block: var(--space-20) var(--space-16);
		border-bottom: 1px solid var(--border-light);
	}

	.hero__eyebrow {
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		color: var(--text-secondary);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		margin-bottom: var(--space-5);
	}

	.hero__title {
		font-size: clamp(3rem, 7vw, 5.5rem);
		font-weight: var(--font-weight-bold);
		line-height: 1.05;
		letter-spacing: -0.03em;
		color: var(--text-primary);
	}

	/* ── Stories ── */
	.stories {
		padding-block: var(--space-20) var(--space-10);
	}

	.story {
		display: grid;
		grid-template-columns: 280px 1fr;
		border-radius: var(--radius-xl);
		border: 1px solid var(--border-light);
		overflow: hidden;
		margin-bottom: var(--space-6);
		text-decoration: none;
		cursor: pointer;
		transition: box-shadow 0.2s ease;
	}

	.story:hover {
		box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
	}

	/* Left panel */
	.story__identity {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--space-4);
		padding: var(--space-10);
		background: var(--bg-secondary);
		border-right: 1px solid var(--border-light);
		overflow: hidden;
	}

.story__logo {
		width: 80px;
		height: 80px;
		border-radius: 50%;
		object-fit: cover;
		box-shadow: 0 0 0 1px rgba(255,255,255,0.12);
		filter: grayscale(1);
		transition: filter 0.3s ease;
	}

	.story:hover .story__logo {
		filter: grayscale(0);
	}

	.story__name {
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: rgba(255,255,255,0.9);
		letter-spacing: -0.01em;
		margin: 0;
	}

	/* Right panel */
	.story__content {
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: var(--space-6);
		padding: var(--space-10) var(--space-12);
		background: var(--bg-secondary);
	}

	.story__quote {
		font-size: clamp(1.1rem, 2vw, 1.35rem);
		line-height: 1.6;
		color: var(--text-primary);
		font-weight: var(--font-weight-medium);
		letter-spacing: -0.01em;
		hyphens: none;
		margin: 0;
	}

	.story__cta {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		color: var(--text-brand);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-semibold);
		text-decoration: none;
	}

	@media (max-width: 640px) {
		.story {
			grid-template-columns: 1fr;
		}

		.story__identity {
			border-right: none;
			border-bottom: 1px solid rgba(255,255,255,0.08);
			padding: var(--space-8);
			flex-direction: row;
			justify-content: flex-start;
		}

		.story__content {
			padding: var(--space-8);
		}
	}

	/* ── Invitation ── */
	.invite {
		padding-block: var(--space-16);
		border-top: 1px solid var(--border-light);
	}

	.invite__inner {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--space-10);
	}

	.invite__text h2 {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		letter-spacing: -0.02em;
		margin-bottom: var(--space-2);
	}

	.invite__text p {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.7;
		max-width: 480px;
	}

	/* ── CTA ── */
	.cta {
		padding-block: var(--space-16);
		border-top: 1px solid var(--border-light);
	}

	.cta__inner {
		max-width: 440px;
	}

	.cta__inner h2 {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		letter-spacing: -0.02em;
		margin-bottom: var(--space-2);
	}

	.cta__inner p {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		margin-bottom: var(--space-6);
	}

	.cta__actions {
		display: flex;
		gap: var(--space-3);
		flex-wrap: wrap;
	}

	/* ── Responsive ── */
	@media (max-width: 768px) {
		.story {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}

		.story__identity {
			flex-direction: row;
			flex-wrap: wrap;
			align-items: center;
			gap: var(--space-4);
			padding-top: 0;
		}

		.story__facts {
			flex-direction: row;
			flex-wrap: wrap;
			gap: var(--space-3);
		}

		.story__facts li::after {
			content: '·';
			margin-left: var(--space-3);
		}

		.story__facts li:last-child::after {
			display: none;
		}

		.invite__inner {
			flex-direction: column;
			align-items: flex-start;
			gap: var(--space-6);
		}
	}
</style>
